import { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Shield, Check, X, Users, Clock, Flag, AlertTriangle, Trash2, RefreshCw, Settings, Church, ChevronDown, Search } from "lucide-react";
import { getMembershipRequests, approveMembership, rejectMembership, getReports, resolveReport, resolveImageUrl, getSpotlightSettings, updateSpotlightSettings, getParishes } from "@/lib/api";
import { toast } from "sonner";
import ParishSettingsTab from "@/components/ParishSettingsTab";

export default function ParishAdminPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [requests, setRequests] = useState([]);
  const [reports, setReports] = useState([]);
  const [spotlightSettings, setSpotlightSettings] = useState({ enabled: true, max_items: 3, rotation_day: 0 });
  const [loading, setLoading] = useState(true);
  const [reportsLoading, setReportsLoading] = useState(true);
  const [spotlightLoading, setSpotlightLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("requests");
  const [allParishes, setAllParishes] = useState([]);
  const [parishSearch, setParishSearch] = useState("");
  
  // Check if user is site admin
  const isSiteAdmin = user?.is_site_admin || user?.role === "admin";
  
  // Get admin parishes from user (for regular parish admins)
  const adminParishes = (user.parishes || []).filter(p => p.is_admin);
  
  // Get current parish from URL or default to first admin parish
  const parishIdFromUrl = searchParams.get('parish');
  const [currentParishId, setCurrentParishId] = useState(
    parishIdFromUrl || user.parish_id || (adminParishes[0]?.parish_id)
  );
  
  // For site admins, find parish from all parishes list
  const currentParish = isSiteAdmin 
    ? allParishes.find(p => p.parish_id === currentParishId)
    : (adminParishes.find(p => p.parish_id === currentParishId) || adminParishes[0]);
  
  // Create a modified user object with the selected parish
  const userWithParish = {
    ...user,
    parish_id: currentParishId,
    parish_name: currentParish?.name
  };

  // Load all parishes for site admins
  useEffect(() => {
    if (isSiteAdmin) {
      loadAllParishes();
    }
  }, [isSiteAdmin]);

  const loadAllParishes = async () => {
    try {
      const res = await getParishes();
      const data = res.data;
      const parishes = Array.isArray(data) ? data : (data.parishes || []);
      setAllParishes(parishes);
      // If no parish selected and we have parishes, select the first one
      if (!currentParishId && parishes.length > 0) {
        setCurrentParishId(parishes[0].parish_id);
      }
    } catch (err) {
      console.error("Failed to load parishes:", err);
    }
  };

  useEffect(() => {
    // Check if user has admin access to any parish
    const hasAdminAccess = isSiteAdmin || adminParishes.length > 0;
    
    if (!hasAdminAccess) {
      navigate("/dashboard");
      return;
    }
    
    if (currentParishId) {
      loadRequests();
      loadReports();
      loadSpotlightSettings();
    }
  }, [user, currentParishId]);

  const handleParishChange = (newParishId) => {
    setCurrentParishId(newParishId);
    navigate(`/parish-admin?parish=${newParishId}`, { replace: true });
  };

  const loadSpotlightSettings = async () => {
    if (!currentParishId) return;
    setSpotlightLoading(true);
    try {
      const res = await getSpotlightSettings(currentParishId);
      setSpotlightSettings(res.data);
    } catch {}
    setSpotlightLoading(false);
  };

  const handleSpotlightToggle = async (enabled) => {
    try {
      await updateSpotlightSettings({ ...spotlightSettings, enabled });
      setSpotlightSettings({ ...spotlightSettings, enabled });
      toast.success(enabled ? "Spotlight enabled" : "Spotlight disabled");
    } catch {
      toast.error("Failed to update settings");
    }
  };

  const handleMaxItemsChange = async (value) => {
    const max_items = parseInt(value);
    try {
      await updateSpotlightSettings({ ...spotlightSettings, max_items });
      setSpotlightSettings({ ...spotlightSettings, max_items });
      toast.success("Max items updated");
    } catch {
      toast.error("Failed to update settings");
    }
  };

  const loadRequests = async () => {
    if (!currentParishId) return;
    setLoading(true);
    try {
      const res = await getMembershipRequests(currentParishId);
      setRequests(res.data);
    } catch (err) {
      toast.error("Failed to load requests");
    }
    setLoading(false);
  };

  const loadReports = async () => {
    setReportsLoading(true);
    try {
      const res = await getReports();
      setReports(res.data);
    } catch {}
    setReportsLoading(false);
  };

  const handleApprove = async (membershipId) => {
    try {
      await approveMembership(currentParishId, membershipId);
      toast.success("Membership approved!");
      setRequests(requests.filter(r => r.membership_id !== membershipId));
    } catch {
      toast.error("Failed to approve");
    }
  };

  const handleReject = async (membershipId) => {
    try {
      await rejectMembership(currentParishId, membershipId);
      toast.success("Membership rejected");
      setRequests(requests.filter(r => r.membership_id !== membershipId));
    } catch {
      toast.error("Failed to reject");
    }
  };

  const handleResolveReport = async (reportId, action) => {
    try {
      await resolveReport(reportId, action);
      toast.success(action === "remove" ? "Content removed" : "Report dismissed");
      setReports(reports.filter(r => r.report_id !== reportId));
    } catch {
      toast.error("Failed to resolve report");
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F7F2]" data-testid="parish-admin-page">
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between gap-3 mb-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md bg-[var(--liturgical-secondary)]/10 flex items-center justify-center">
              <Shield className="w-5 h-5 text-[var(--liturgical-secondary)]" />
            </div>
            <div>
              <h1 className="font-['Crimson_Text'] text-3xl font-bold text-[#2D2A26] tracking-tight" data-testid="admin-title">Parish Administration</h1>
              <p className="text-sm text-[#6B665E] font-['Nunito']">{currentParish?.name || "Select a parish"}</p>
            </div>
          </div>
          
          {/* Parish Selector - Show for site admins or if admin of multiple parishes */}
          {(isSiteAdmin || adminParishes.length > 1) && (
            <Select value={currentParishId} onValueChange={handleParishChange}>
              <SelectTrigger className="w-auto min-w-[200px] border-[#E5E0D5] font-['Nunito']" data-testid="parish-selector">
                <div className="flex items-center gap-2">
                  <Church className="w-4 h-4 text-[var(--liturgical-primary)]" />
                  <SelectValue placeholder="Select parish" />
                </div>
              </SelectTrigger>
              <SelectContent className="bg-white border-[#E5E0D5] max-h-[300px]">
                {isSiteAdmin && (
                  <div className="px-2 py-1.5 sticky top-0 bg-white border-b">
                    <Input 
                      placeholder="Search parishes..."
                      value={parishSearch}
                      onChange={(e) => setParishSearch(e.target.value)}
                      className="h-8 text-sm"
                    />
                  </div>
                )}
                {(isSiteAdmin ? allParishes : adminParishes)
                  .filter(p => !parishSearch || p.name?.toLowerCase().includes(parishSearch.toLowerCase()))
                  .map((parish) => (
                  <SelectItem 
                    key={parish.parish_id} 
                    value={parish.parish_id}
                    className="font-['Nunito']"
                  >
                    {parish.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} data-testid="admin-tabs">
          <TabsList className="bg-[#F0EBE0] border border-[#E5E0D5] mb-6 flex-wrap">
            <TabsTrigger value="requests" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">
              <Users className="w-4 h-4 mr-2" />
              Requests {requests.length > 0 && <Badge className="ml-1.5 bg-[var(--liturgical-primary)] text-white text-[10px] h-5">{requests.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="reports" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">
              <Flag className="w-4 h-4 mr-2" />
              Reports {reports.length > 0 && <Badge className="ml-1.5 bg-[var(--liturgical-secondary)] text-[#2D2A26] text-[10px] h-5">{reports.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="parish" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">
              <Church className="w-4 h-4 mr-2" />
              Parish Info
            </TabsTrigger>
            <TabsTrigger value="spotlight" className="font-['Nunito'] text-sm data-[state=active]:bg-white data-[state=active]:text-[var(--liturgical-primary)]">
              <RefreshCw className="w-4 h-4 mr-2" />
              Spotlight
            </TabsTrigger>
          </TabsList>

          {/* Membership Requests Tab */}
          <TabsContent value="requests">
            <Card className="bg-white border-[#E5E0D5] shadow-sm" data-testid="membership-requests-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                  <Users className="w-5 h-5 text-[var(--liturgical-primary)]" />
                  Membership Requests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="text-center py-8 text-[#6B665E] font-['Nunito']">Loading requests...</div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-requests">
                    <Clock className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                    <p className="text-sm text-[#6B665E] font-['Nunito']">No pending membership requests</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {requests.map(req => (
                      <div key={req.membership_id} className="flex items-center gap-4 p-4 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]" data-testid={`request-${req.membership_id}`}>
                        <Avatar className="h-10 w-10 border border-[#E5E0D5]">
                          <AvatarImage src={req.user?.picture} />
                          <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-sm">{req.user?.name?.[0]?.toUpperCase()}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">{req.user?.name}</p>
                          <p className="text-xs text-[#6B665E] font-['Nunito']">{req.user?.email}</p>
                          <p className="text-[10px] text-[#6B665E] font-['Nunito'] mt-0.5">Requested {new Date(req.created_at).toLocaleDateString()}</p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleApprove(req.membership_id)}
                            className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white font-['Nunito'] text-xs h-8 px-3 active:scale-95"
                            data-testid={`approve-${req.membership_id}`}
                          >
                            <Check className="w-3 h-3 mr-1" /> Approve
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleReject(req.membership_id)}
                            className="border-[#991B1B] text-[#991B1B] hover:bg-[#991B1B]/5 font-['Nunito'] text-xs h-8 px-3"
                            data-testid={`reject-${req.membership_id}`}
                          >
                            <X className="w-3 h-3 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Reports Tab */}
          <TabsContent value="reports">
            <Card className="bg-white border-[#E5E0D5] shadow-sm" data-testid="content-reports-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                  <Flag className="w-5 h-5 text-[var(--liturgical-secondary)]" />
                  Reported Content
                </CardTitle>
              </CardHeader>
              <CardContent>
                {reportsLoading ? (
                  <div className="text-center py-8 text-[#6B665E] font-['Nunito']">Loading reports...</div>
                ) : reports.length === 0 ? (
                  <div className="text-center py-12" data-testid="no-reports">
                    <Flag className="w-10 h-10 text-[#E5E0D5] mx-auto mb-2" />
                    <p className="text-sm text-[#6B665E] font-['Nunito']">No reported content to review</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reports.map(report => (
                      <div key={report.report_id} className="p-4 bg-[#F9F7F2] rounded-md border border-[#E5E0D5]" data-testid={`report-${report.report_id}`}>
                        <div className="flex items-start gap-3 mb-3">
                          <div className="w-8 h-8 rounded-full bg-[var(--liturgical-secondary)]/10 flex items-center justify-center flex-shrink-0">
                            <AlertTriangle className="w-4 h-4 text-[var(--liturgical-secondary)]" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Badge variant="outline" className="border-[#E5E0D5] text-[#6B665E] text-[10px] font-['Nunito'] capitalize">
                                {report.content_type}
                              </Badge>
                              <span className="text-xs text-[#6B665E] font-['Nunito']">
                                Reported by {report.reporter?.name}
                              </span>
                            </div>
                            <p className="text-xs text-[var(--liturgical-primary)] font-['Nunito'] font-semibold">
                              Reason: {report.reason}
                            </p>
                            {report.details && (
                              <p className="text-xs text-[#6B665E] font-['Nunito'] mt-1">{report.details}</p>
                            )}
                          </div>
                        </div>
                        {/* Content Preview */}
                        <div className="bg-white rounded border border-[#E5E0D5] p-3 mb-3">
                          {report.content_type === "post" ? (
                            <div>
                              <p className="text-sm text-[#2D2A26] font-['Nunito'] line-clamp-3">{report.content?.content}</p>
                              {report.content?.image_url && (
                                <img src={resolveImageUrl(report.content.image_url)} alt="" className="mt-2 max-h-32 rounded border border-[#E5E0D5]" />
                              )}
                            </div>
                          ) : (
                            <div className="flex items-center gap-3">
                              {report.content?.image_url && (
                                <img src={resolveImageUrl(report.content.image_url)} alt="" className="w-16 h-16 object-cover rounded" />
                              )}
                              <div>
                                <p className="text-sm font-semibold text-[#2D2A26] font-['Nunito']">{report.content?.title}</p>
                                <p className="text-xs text-[#6B665E] font-['Nunito'] line-clamp-1">{report.content?.description}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleResolveReport(report.report_id, "dismiss")}
                            className="border-[#E5E0D5] text-[#6B665E] hover:bg-[#F0EBE0] font-['Nunito'] text-xs h-8"
                            data-testid={`dismiss-report-${report.report_id}`}
                          >
                            <X className="w-3 h-3 mr-1" /> Dismiss
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleResolveReport(report.report_id, "remove")}
                            className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-['Nunito'] text-xs h-8"
                            data-testid={`remove-content-${report.report_id}`}
                          >
                            <Trash2 className="w-3 h-3 mr-1" /> Remove Content
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Parish Settings Tab */}
          <TabsContent value="parish">
            <ParishSettingsTab user={userWithParish} />
          </TabsContent>

          {/* Spotlight Settings Tab */}
          <TabsContent value="spotlight">
            <Card className="bg-white border-[#E5E0D5] shadow-sm" data-testid="spotlight-settings-card">
              <CardHeader className="pb-3">
                <CardTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26] flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-[var(--liturgical-primary)]" />
                  Weekly Spotlight Settings
                </CardTitle>
              </CardHeader>
              <CardContent>
                {spotlightLoading ? (
                  <div className="text-center py-8 text-[#6B665E] font-['Nunito']">Loading settings...</div>
                ) : (
                  <div className="space-y-6">
                    <div className="bg-[#F9F7F2] rounded-lg p-4 border border-[#E5E0D5]">
                      <p className="text-sm text-[#2D2A26] font-['Nunito'] mb-2">
                        The Weekly Spotlight automatically showcases listings from your parish to all users. 
                        Items rotate every Monday to ensure fair exposure for all sellers.
                      </p>
                      <p className="text-xs text-[#6B665E] font-['Nunito']">
                        Selection is randomized each week - no manual curation needed!
                      </p>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[#E5E0D5]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                          <RefreshCw className={`w-5 h-5 ${spotlightSettings.enabled ? 'text-[var(--liturgical-primary)]' : 'text-[#6B665E]'}`} />
                        </div>
                        <div>
                          <Label className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">Enable Weekly Spotlight</Label>
                          <p className="text-xs text-[#6B665E] font-['Nunito']">Show your parish listings in the spotlight section</p>
                        </div>
                      </div>
                      <Switch
                        checked={spotlightSettings.enabled}
                        onCheckedChange={handleSpotlightToggle}
                        className="data-[state=checked]:bg-[var(--liturgical-primary)]"
                        data-testid="spotlight-toggle"
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-white rounded-lg border border-[#E5E0D5]">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[var(--liturgical-primary)]/10 flex items-center justify-center">
                          <Settings className="w-5 h-5 text-[var(--liturgical-primary)]" />
                        </div>
                        <div>
                          <Label className="font-['Nunito'] text-sm font-semibold text-[#2D2A26]">Max Items Per Week</Label>
                          <p className="text-xs text-[#6B665E] font-['Nunito']">Maximum listings to show from your parish</p>
                        </div>
                      </div>
                      <Select 
                        value={spotlightSettings.max_items?.toString()} 
                        onValueChange={handleMaxItemsChange}
                        disabled={!spotlightSettings.enabled}
                      >
                        <SelectTrigger className="w-20 border-[#E5E0D5] text-sm font-['Nunito']" data-testid="spotlight-max-items">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#E5E0D5]">
                          <SelectItem value="1" className="font-['Nunito'] text-sm">1</SelectItem>
                          <SelectItem value="2" className="font-['Nunito'] text-sm">2</SelectItem>
                          <SelectItem value="3" className="font-['Nunito'] text-sm">3</SelectItem>
                          <SelectItem value="4" className="font-['Nunito'] text-sm">4</SelectItem>
                          <SelectItem value="5" className="font-['Nunito'] text-sm">5</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="bg-[var(--liturgical-primary)]/5 rounded-lg p-4 border border-[var(--liturgical-primary)]/20">
                      <p className="text-xs text-[var(--liturgical-primary)] font-['Nunito'] font-semibold mb-1">How it works:</p>
                      <ul className="text-xs text-[#6B665E] font-['Nunito'] space-y-1 list-disc list-inside">
                        <li>Items are randomly selected from active parish listings</li>
                        <li>Selection changes every Monday at midnight UTC</li>
                        <li>All parishes participate - no favoritism</li>
                        <li>Sold items are automatically excluded</li>
                      </ul>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
